import React from 'react';
import { Box, Paper, Typography, Skeleton, Divider } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { formatCurrency } from '../../utils/Currency';

const ComparisonColumn = ({ title, data, currency, showValues, loading, isCurrentPeriod }) => {
  const theme = useTheme();

  const metrics = [
    {
      label: 'Receita',
      value: data?.cashIn || 0,
      format: 'currency',
      color: theme.palette.success.main,
    },
    {
      label: 'Despesa',
      value: data?.cashOut || 0,
      format: 'currency',
      color: theme.palette.error.main,
    },
    {
      label: 'Saldo',
      value: data?.netBalance || 0,
      format: 'currency',
      color: theme.palette.info.main,
    },
    {
      label: 'Poupanca',
      value: data?.savingsRate || 0,
      format: 'percentage',
      color: theme.palette.warning.main,
    },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        backgroundColor: isCurrentPeriod
          ? alpha(theme.palette.primary.main, 0.04)
          : 'transparent',
        border: isCurrentPeriod
          ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          : `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          mb: 2,
          color: isCurrentPeriod
            ? theme.palette.primary.main
            : theme.palette.text.secondary,
        }}
      >
        {loading ? <Skeleton width={100} /> : title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {metrics.map((metric) => (
          <Box key={metric.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {metric.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: metric.color,
              }}
            >
              {loading ? (
                <Skeleton width={80} />
              ) : showValues ? (
                metric.format === 'currency'
                  ? formatCurrency(currency, metric.value)
                  : `${metric.value.toFixed(1)}%`
              ) : (
                '--'
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const PeriodComparison = ({ currentPeriod, previousPeriod, comparison, currency, showValues, loading, viewMode }) => {
  const theme = useTheme();

  const currentTitle = viewMode === 'annual'
    ? `Ano ${currentPeriod?.period || ''}`
    : `${currentPeriod?.period || 'Este Mes'}`;

  const previousTitle = viewMode === 'annual'
    ? `Ano ${previousPeriod?.period || ''}`
    : `${previousPeriod?.period || 'Mes Anterior'}`;

  const getChangeIndicator = (value, inverted = false) => {
    if (!value || !showValues) return null;
    const isPositive = inverted ? value < 0 : value > 0;
    return isPositive ? (
      <TrendingUpIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />
    ) : (
      <TrendingDownIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />
    );
  };

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        boxShadow: theme.shadows[2],
        height: '100%',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 1,
          color: theme.palette.text.secondary,
          mb: 2,
          display: 'block',
        }}
      >
        Comparativo de Periodos
      </Typography>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <ComparisonColumn
          title={currentTitle}
          data={currentPeriod}
          currency={currency}
          showValues={showValues}
          loading={loading}
          isCurrentPeriod={true}
        />
        <ComparisonColumn
          title={previousTitle}
          data={previousPeriod}
          currency={currency}
          showValues={showValues}
          loading={loading}
          isCurrentPeriod={false}
        />
      </Box>

      {comparison && showValues && !loading && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getChangeIndicator(comparison.cashInChange)}
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Receita: {(comparison.cashInChange ?? 0) > 0 ? '+' : ''}{(comparison.cashInChange ?? 0).toFixed(1)}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getChangeIndicator(comparison.cashOutChange, true)}
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Despesa: {(comparison.cashOutChange ?? 0) > 0 ? '+' : ''}{(comparison.cashOutChange ?? 0).toFixed(1)}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getChangeIndicator(comparison.savingsRateChange)}
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Poupanca: {(comparison.savingsRateChange ?? 0) > 0 ? '+' : ''}{(comparison.savingsRateChange ?? 0).toFixed(1)}pp
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default PeriodComparison;
